import { TopicsService } from "@/services/CoachingServices";
import topicLoader from "./topicLoader";

export default {
  mixins: [topicLoader],

  data() {
    return {
      scenarios: [],
      scenarioCounts: new Map(),
      selectedScenarioId: "",
      // Add a cache for scenarios
      scenariosCache: new Map(),
    };
  },

  computed: {
    selectedScenario() {
      return (
        this.scenarios.find((s) => s.id === this.selectedScenarioId) || null
      );
    },
  },

  watch: {
    topicIds: {
      immediate: true,
      async handler(newTopicIds) {
        if (!newTopicIds) return;

        // Preload scenarios for all topics
        await Promise.all(
          newTopicIds.map(async (topicId) => {
            if (!this.scenariosCache.has(topicId)) {
              const scenarios = await this.fetchScenarios(topicId);
              this.scenariosCache.set(topicId, scenarios);
            }
          })
        );
      },
    },

    selectedTopicId: {
      immediate: true,
      async handler(topicId) {
        if (!topicId) {
          this.scenarios = [];
          return;
        }

        const scenarios = await this.loadScenarios(topicId);

        if (scenarios.length === 0) {
          this.selectedScenarioId = null;
          return;
        }

        if (
          this.selectedScenarioId !== null &&
          scenarios.some((s) => s.id === this.selectedScenarioId)
        ) {
          return;
        }

        const initialScenarioId = this.$route.query["scenarioId"] || null;
        if (
          initialScenarioId &&
          scenarios.find((t) => t.id === initialScenarioId)
        ) {
          this.handleSelectScenario(initialScenarioId);
          return;
        }

        this.handleSelectScenario(scenarios[0].id);
      },
    },
  },

  methods: {
    // New method to fetch scenarios without updating state
    async fetchScenarios(topicId) {
      try {
        const scenarios = await TopicsService.getScenarios(topicId);
        if (scenarios.length > 0) {
          this.scenarioCounts.set(topicId, scenarios.length);
        }
        return scenarios;
      } catch (error) {
        console.error("Error loading scenarios:", error);
        return [];
      }
    },

    async getOrFetchScenarios(topicId) {
      if (this.scenariosCache.has(topicId)) {
        return this.scenariosCache.get(topicId);
      }
      const scenarios = await this.fetchScenarios(topicId);
      this.scenariosCache.set(topicId, scenarios);
      return scenarios;
    },

    async loadScenarios(topicId = this.selectedTopicId) {
      if (!topicId) return [];

      try {
        const scenarios = await this.getOrFetchScenarios(topicId);
        this.scenarios = scenarios;
        return scenarios;
      } catch (error) {
        console.error("Error loading scenarios:", error);
        this.$buefy.toast.open({
          message: "Failed to load scenarios",
          type: "is-danger",
        });
        this.scenarios = [];
        return [];
      }
    },

    async handlePreviewTopic(topicId) {
      await this.handleSelectTopic(topicId);
      setTimeout(() => {
        this.handleSelectScenario(null);
      }, 100);
    },

    async handleSelectTopic(topicId) {
      if (this.selectedTopicId === topicId) return;
      if (this.topics.find((t) => t.id === topicId) === undefined) return;

      this.selectedTopicId = topicId;
      this.$emit("topic-selected", topicId);
    },

    handleSelectScenario(scenarioId) {
      if (this.selectedScenarioId === scenarioId) return;
      this.selectedScenarioId = scenarioId;
      this.$emit("scenario-selected", scenarioId);
    },

    clearSelections() {
      this.clearTopicSelection(); // From topicLoader
      this.selectedScenarioId = null;
      this.scenarios = [];
      // Clear the cache when selections are cleared
      this.scenariosCache.clear();
    },
  },
};
